"use client";

import React from "react";
import { MonitorPlay, Volume2, Info, Loader, CircleAlert } from "lucide-react";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function AVProductsConsumer() {
  const {
    products,
    loading,
    error,
    refetch
  } = useEcommerceProducts();
  if (loading) {
    return <div data-section-id="146705" className="grid grid-cols-1 gap-8 md:grid-cols-2">
        {[...Array(4)].map((_, i) => <Card key={i} data-index={i} className="overflow-hidden border-border/20 bg-card/50 backdrop-blur-sm animate-pulse">
            <AspectRatio ratio={16 / 9} className="bg-muted/30" />
            <CardHeader className="space-y-4">
              <div className="h-6 w-2/3 rounded bg-muted/30" />
              <div className="h-4 w-1/2 rounded bg-muted/30" />
            </CardHeader>
            <CardContent>
              <div className="h-8 w-1/4 rounded bg-muted/30" />
            </CardContent>
            <CardFooter>
              <div className="h-10 w-full rounded bg-muted/30" />
            </CardFooter>
          </Card>)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center rounded-xl border border-destructive/20 bg-destructive/5 p-8 text-center">
        <CircleAlert className="mb-4 h-8 w-8 text-destructive" />
        <p className="mb-4 text-sm text-destructive-foreground">Unable to retrieve equipment specifications.</p>
        <Button variant="outline" onClick={refetch} className="border-primary/20 hover:bg-primary/5">Try Again</Button>
      </div>;
  }
  if (products.length === 0) {
    return <div className="flex flex-col items-center justify-center rounded-xl border border-border/30 bg-card/30 p-12 text-center backdrop-blur-sm">
        <MonitorPlay className="mb-4 h-12 w-12 text-secondary/40" />
        <h3 className="mb-2 text-lg font-serif font-semibold text-foreground">Specifications Pending</h3>
        <p className="text-sm text-muted-foreground">We are finalizing the technical details for our Peliatan venue. Please inquire for custom setups.</p>
      </div>;
  }
  return <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
      {products.map((product, i) => <Card key={product.id} data-index={i} className="group relative overflow-hidden border-border/30 bg-card/60 transition-all hover:border-secondary/40 hover:bg-card/80 hover:shadow-[0_10px_40px_-15px_rgba(45,35,20,0.1)] backdrop-blur-sm">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-background/40 z-10 pointer-events-none" />
          
          {product.featured_image_url ? <AspectRatio ratio={16 / 9} className="overflow-hidden bg-muted/30">
              <img src={product.featured_image_url} alt={product.title} className="h-full w-full object-cover transition-transform duration-1000 group-hover:scale-105" loading="lazy" />
            </AspectRatio> : <AspectRatio ratio={16 / 9} className="flex items-center justify-center bg-muted/20">
              <MonitorPlay className="h-16 w-16 text-secondary/20" />
            </AspectRatio>}

          <div className="relative z-20 flex h-full flex-col justify-between">
            <CardHeader>
              <div className="mb-2 flex items-center gap-2">
                <span className="inline-flex items-center rounded-full border border-secondary/20 bg-secondary/10 px-2.5 py-0.5 text-xs font-semibold text-secondary">
                  Venue Standard
                </span>
              </div>
              <CardTitle className="text-2xl font-serif font-bold tracking-tight text-foreground">
                <Link to={product.link} className="hover:text-secondary transition-colors">
                  {product.title}
                </Link>
              </CardTitle>
              
              {}
              {product.short_description && <div className="mt-4 font-sans text-sm leading-relaxed text-muted-foreground [&_p]:mb-2 [&_p:last-child]:mb-0 [&_strong]:text-primary [&_strong]:font-semibold" dangerouslySetInnerHTML={{
            __html: product.short_description
          }} />}
            </CardHeader>

            <div className="mt-auto">
              <CardContent>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-serif font-bold text-primary">
                    ${product.price?.toFixed(2) || "0.00"}
                  </span>
                  <span className="text-xs uppercase tracking-widest text-muted-foreground">Booking Fee</span>
                </div>
              </CardContent>

              <CardFooter className="flex flex-col gap-3 sm:flex-row">
                <AddToCartButton productId={product.id} className="w-full sm:w-auto sm:flex-1" buttonClassName="bg-primary text-primary-foreground hover:bg-primary/90 w-full font-semibold transition-all" text="Add to Inquiry" showQuantity={false} />
                <Button variant="outline" asChild className="w-full sm:w-auto border-border/60 hover:bg-accent hover:text-accent-foreground transition-colors">
                  <Link to={product.link}>
                    <Info className="mr-2 h-4 w-4" />
                    Technical Specs
                  </Link>
                </Button>
              </CardFooter>
            </div>
          </div>
        </Card>)}
    </div>;
}
export default function AudioAndVisualTechnology() {
  const wp_query_params = {
    product_cat: [18],
    per_page: 4,
    status: "publish"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="event-capabilities" className="bg-background text-foreground relative overflow-hidden py-24 lg:py-32">
      {}
      <div className="absolute left-1/2 top-1/2 -z-10 h-[800px] w-[800px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-secondary/5 blur-[120px] pointer-events-none" />
      <div className="absolute right-0 top-0 -z-10 h-[500px] w-[500px] translate-x-1/3 -translate-y-1/3 rounded-full bg-primary/5 blur-[100px] pointer-events-none" />

      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-16 max-w-3xl">
          <div className="mb-4 flex items-center gap-2 text-secondary">
            <Volume2 className="h-5 w-5" />
            <span className="font-sans text-xs font-bold uppercase tracking-[0.2em]">Venue Infrastructure</span>
          </div>
          <h2 className="mb-6 text-4xl font-serif font-bold tracking-tight text-primary sm:text-5xl lg:text-6xl">
            Event Capabilities & Technology
          </h2>
          <p className="text-lg text-muted-foreground font-sans leading-relaxed">
            Ubud Event Space provides a sophisticated blend of organic bamboo architecture and modern technical infrastructure. From high-fidelity sound systems to flexible layout configurations, our Peliatan venue is equipped to handle intimate workshops, private dining, and medium-sized celebrations with professional excellence.
          </p>
        </div>

        <EcommerceProductsProvider wp_query={{
        product_cat: [18],
        per_page: 4,
        status: ["publish"]
      }} postType="product" className="relative z-10">
          <AVProductsConsumer />
        </EcommerceProductsProvider>
      </div>
    </section>;
}